import { useState, useEffect } from 'react';

interface PreloaderProps {
  onComplete: () => void;
}

export default function Preloader({ onComplete }: PreloaderProps) {
  const [phase, setPhase] = useState<'init' | 'line' | 'name' | 'subtitle' | 'exit' | 'done'>('init');

  useEffect(() => {
    const t1 = setTimeout(() => setPhase('line'), 200);
    const t2 = setTimeout(() => setPhase('name'), 1000);
    const t3 = setTimeout(() => setPhase('subtitle'), 1500);
    const t4 = setTimeout(() => setPhase('exit'), 2600);
    const t5 = setTimeout(() => {
      setPhase('done');
      onComplete();
    }, 3400);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
      clearTimeout(t5);
    };
  }, [onComplete]);

  if (phase === 'done') return null;

  const visible = phase === 'line' || phase === 'name' || phase === 'subtitle' || phase === 'exit';

  return (
    <div
      className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center transition-all duration-1000 ease-in-out ${
        phase === 'exit' ? 'opacity-0' : 'opacity-100'
      }`}
      style={{ backgroundColor: '#0E1412' }}
    >
      {/* Ultra subtle grain */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat',
          backgroundSize: '256px 256px',
          opacity: 0.025,
        }}
      />

      {/* Ambient golden glow */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
        style={{
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle, rgba(200,169,106,0.06) 0%, transparent 70%)',
          opacity: visible ? 1 : 0,
          transition: 'opacity 1s ease',
        }}
      />

      <div
        className={`relative flex flex-col items-center px-6 transition-all duration-1000 ease-out ${
          phase === 'exit'
            ? 'scale-105 opacity-0'
            : visible
              ? 'scale-100 opacity-100'
              : 'scale-95 opacity-0'
        }`}
      >
        {/* Elegant thin line that draws in */}
        <div className="w-[200px] h-[1px] mb-8 overflow-hidden">
          <div
            className="h-full"
            style={{
              backgroundColor: '#C8A96A',
              width: phase === 'line' || phase === 'name' || phase === 'subtitle' || phase === 'exit' ? '100%' : '0%',
              transition: 'width 1.4s cubic-bezier(0.22, 1, 0.36, 1)',
              opacity: 0.6,
            }}
          />
        </div>

        {/* Name */}
        <div
          className={`transition-all duration-800 ease-out ${
            phase === 'name' || phase === 'subtitle' || phase === 'exit'
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-3'
          }`}
        >
          <h2
            className="text-xl md:text-2xl tracking-[0.3em] text-center select-none"
            style={{
              fontFamily: 'var(--font-heading)',
              color: '#F8F7F5',
              fontWeight: 400,
              opacity: 0.85,
            }}
          >
            Dra. Melissa Ciríaco
          </h2>
        </div>

        {/* Subtitle */}
        <div
          className={`mt-3 transition-all duration-800 ease-out ${
            phase === 'subtitle' || phase === 'exit'
              ? 'opacity-70 translate-y-0'
              : 'opacity-0 translate-y-2'
          }`}
        >
          <p
            className="text-[10px] md:text-[11px] tracking-[0.15em] md:tracking-[0.2em] text-center select-none"
            style={{
              fontFamily: 'var(--font-body)',
              color: 'rgba(248, 247, 245, 0.35)',
              fontWeight: 300,
            }}
          >
            Emagrecimento · Lipedema · Reposição Hormonal
          </p>
        </div>
      </div>
    </div>
  );
}